/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useEffect
} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  Entity,
  Roles,
  Users
} from '@ion/core/src/types/entities';
import {toISOString} from '@ion/core/src/utils/DateUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {
  useExtendStyles
} from '../hooks/useExtendStyles';
import {
  useFormSave
} from '../hooks/useFormSave';
import {
  useFormState
} from '../hooks/useFormState';
import {
  useHasChange
} from '../hooks/useHasChange';
import {
  useMergeState
} from '../hooks/useMergeState';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {dlsColor} from '../theme/theme';
import {IconSize} from '../types/types';
import CancelButton from './controls/CancelButton';
import SaveButton from './controls/SaveButton';
import {DatePickerTextConfig} from './datetimepicker/DatePicker';
import DatePicker from './datetimepicker/DatePickerExporter';
import {
  getInitialState,
  serialize,
  TargetType,
  UserFormFields
} from './BulkUserEditorUtils';
import styles, {getClasses} from './editorStyles';
import EntityStatusChooser from './EntityStatusChooser';
import GroupChooser from './GroupChooser';
import RoleChooser from './RoleChooser';

// defines the prop definitions for bulk schedule editor ...
export interface BulkUserEditorProps extends DatePickerTextConfig {
  open: boolean;
  users: Users;
  roles: Roles;
  account: Account;
  clear?: boolean;
  loading?: boolean;
  onSave: (users: Users) => void;
  onCancel?: (event: SyntheticEvent<{}, Event>) => void;
};

/**
  * @BulkUserEditor
  * The Schedule Form with set fields and encapsulated logic for handling the form's state,
  * including validation.
  *
  * @param {boolean}  open Determines if the BulkUserEditor dialog should be open.
  * @param {Account}  account User's account object.
  * @param {Users}    users The selected schedules for edit.
  * @param {Roles}    roles The list of all available roles.
  * @param {Function} onCancel Callback function fired when the dialog is to be closed.
  * @param {Function} onSave Function to be run when the form is submitted. The function
  *                   is passed an Schedule object.
  * @param {boolean}  clear If true we clear the form state.
  * @param {boolean}  loading disables any for submission while the form is busy.
 */
export const BulkUserEditor = ({
  open,
  users,
  roles,
  account,
  onSave,
  onCancel,
  validDateFormats,
  clear = false,
  loading = false,
  allowManualDateEntry = false
}: BulkUserEditorProps) => {
  const editorStyles                          = useExtendStyles(styles);
  const classes                               = editorStyles();
  const [formState, setFormState]             = useFormState(getInitialState({}, users, roles));
  const [openDatePickers, setOpenDatePickers] = useMergeState();
  const hasChange                             = useHasChange(formState);

  const onSubmit = useFormSave({
    serialize,
    formState,
    setFormState,
    onSave
  });

  function clearForm() {
    setFormState({}, true);
  };

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  function handleChooserChange({name, value, required = false}: TargetType) {
    setFormState({
      [name] : {
        name,
        value : isValue(value, 'none') ? null : value,
        required
      }
    });
  };

  function handleDateChange({name, value, required = false}) {
    setFormState({[name]: {name, value: toISOString(value), required}});
  };

  return (
    <Dialog open={open}
      onBackdropClick={null}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onCancel} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {translate('common:editors.bulkEdit.title', {
              count  : users?.length,
              entity : translate('common:entityLabels.users')
            })}
          </Typography>
        </div>
      </DialogTitle>
      <form className={classes.form} onSubmit={onSubmit}>
        <div className={classes.formWrapper}>
          <DialogContent className={classes.formDialogContent}>
            <FormSection>
              <FormItem halfWidth>
                <GroupChooser account={account}
                  onChange={function(value) {
                    handleChooserChange({
                      name : UserFormFields.Groups,
                      value
                    });
                  }}
                  hasError={formState[UserFormFields.Groups]?.error} />
              </FormItem>
              <FormItem halfWidth>
                <EntityStatusChooser entity={Entity.user}
                  onChange={function(value) {
                    handleChooserChange({
                      name : UserFormFields.Status,
                      value
                    });
                  }} />
              </FormItem>
              <FormItem halfWidth className={classes.datePickerItem}>
                <DatePicker closeOnClickOutside
                  useDatePickerTextField
                  manualInput={allowManualDateEntry}
                  isDatePicker={allowManualDateEntry}
                  allowManualEntry={allowManualDateEntry}
                  validDateTimeFormats={validDateFormats}
                  fieldLabel={translate('common:editors.endDate.label')}
                  placeholder={translate('common:editors.endDate.placeholder')}
                  open={openDatePickers[UserFormFields.EndDate]}
                  toggle={function() {
                    setOpenDatePickers(
                      {[UserFormFields.EndDate]: !openDatePickers[UserFormFields.EndDate]}
                    );
                  }}
                  onChange={function(value, required) {
                    handleDateChange({
                      name : UserFormFields.EndDate,
                      value,
                      required
                    });
                  }}
                  defaultValue={formState[UserFormFields.EndDate]?.value} />
              </FormItem>
              <FormItem halfWidth>
                <RoleChooser readOnly={false}
                  useDisabledStyles={false}
                  accountId={entityId(account)}
                  appKey={get('appKey')}
                  currentGroup={entityId(account)}
                  onChange={function(value) {
                    handleChooserChange({
                      name : UserFormFields.Role,
                      value
                    });
                  }}
                  hasError={formState[UserFormFields.Role]?.error}
                  defaultValue={formState[UserFormFields.Role]?.value} />
              </FormItem>
            </FormSection>
          </DialogContent>
        </div>
      </form>
      <DialogActions className={classes.formActions}>
        <CancelButton onCancel={onCancel}
          clearForm={clearForm}
          disabled={loading} />
        <SaveButton onSave={onSubmit}
          disabled={loading || !hasChange}
          loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

export default BulkUserEditor;
